import type { QueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';

import type { FilterDef, FilterOption } from '@nucleus-commerce/admin-kit/index-table';
import {
  HelpArticleStatus,
  HelpVisibility,
  type HelpArticle,
  type HelpCategory,
} from '@nucleus-commerce/api-client/admin';

import { helpCategoriesQueryOptions } from '../hooks/use-help-categories';

/** Fields GET /help/articles sorts on; anything else is a 400. */
export const HELP_ARTICLE_SORTS = ['position', 'updatedAt', 'title', 'views'] as const;

const valuesOf = (values: Record<string, string>): FilterOption[] =>
  Object.values(values).map((value) => ({ value, label: value }));

/**
 * The filters as the URL and the API spell them, for the route's search schema and loader. The
 * enum `options` drop unknown values the same way the page's filters do, so the prefetch and the
 * page share a query key.
 */
export const HELP_ARTICLE_FILTER_FIELDS = [
  { key: 'status', type: 'enum', options: valuesOf(HelpArticleStatus) },
  { key: 'visibility', type: 'enum', options: valuesOf(HelpVisibility) },
  { key: 'categoryId', type: 'async' },
] as const;

const categoryOption = (category: HelpCategory): FilterOption => ({
  value: category.id,
  label: category.name,
});

export interface HelpArticleFiltersOptions {
  /** The `helpCenter` namespace. */
  t: TFunction;
  queryClient: QueryClient;
  storeId: string;
}

/** Filters of the articles table, in the order the filter menu lists them. */
export function helpArticleFilters({
  t,
  queryClient,
  storeId,
}: HelpArticleFiltersOptions): Array<FilterDef<HelpArticle>> {
  // Categories are bounded (50 per store) and served whole, so the picker searches them here.
  const categories = async () =>
    (await queryClient.query(helpCategoriesQueryOptions(storeId))).map(categoryOption);

  return [
    {
      key: 'status',
      label: t('filters.status'),
      type: 'enum',
      pinned: true,
      options: valuesOf(HelpArticleStatus).map(({ value }) => ({
        value,
        label: t(`status.${value}`),
      })),
    },
    {
      key: 'visibility',
      label: t('filters.visibility'),
      type: 'enum',
      pinned: true,
      options: valuesOf(HelpVisibility).map(({ value }) => ({
        value,
        label: t(`visibility.${value}`),
      })),
    },
    {
      key: 'categoryId',
      label: t('filters.category'),
      type: 'async',
      pinned: true,
      aliases: ['category'],
      debounceMs: 0,
      loadOptions: async (query) => {
        const needle = query.trim().toLowerCase();
        const options = await categories();
        return needle ? options.filter((o) => o.label.toLowerCase().includes(needle)) : options;
      },
      resolveOptions: async (values) =>
        (await categories()).filter((o) => values.includes(o.value)),
    },
  ];
}
